import React from "react";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  Input,
  InputProps,
  AddressInput
} from "collections/Form";
import i18n, { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { useNotification, DISMISS } from "collections/Notifications";
import { LoadingIndicator, CategoryTitle, SubmitButton } from "elements";
import {
  useUpdateSiteMutation,
  useSiteNameAndAddressQuery
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { Redirect } from "polyrouter";
import { object, string, InferType, bool } from "yup";
import { needs, useGuard } from "rbac";
import BlockingForm from "collections/Form/BlockingForm";
import { SettingsCategory } from "../../Inventory/Settings-Shared/DeviceSettingsComponents";
import { UNASSIGNED } from "Constants";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export const FormInput: React.FC<InputProps> = ({ name, label, ...rest }) => {
  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={name + "-input"}>
        {label}:
      </label>
      <div className="col-4">
        <Input {...rest} name={name} data-testid={name} />
      </div>
    </div>
  );
};

const GeneralSettingsSchema = object({
  siteName: string()
    .label("NAME")
    .required()
    .test(UNASSIGNED, i18n.t("ERROR_UNASSIGNED_SITE_NAME"), function(
      value: string
    ) {
      const siteNameUnassigned = this.parent.siteNameUnassigned;
      if (siteNameUnassigned) return true;
      return !nameIsUnassigned(value);
    })

    .ensure(),
  siteAddress: object({
    address1: string()
      .label("SITE_ADDRESS")
      .notRequired(),
    city: string()
      .label("CITY")
      .notRequired(),
    region: string()
      .label("STATE")
      .notRequired(),
    postalCode: string()
      .label("POSTAL_CODE")
      .notRequired(),
    country: string()
      .label("COUNTRY")
      .notRequired(),
    geohash: string()
      .label("geohash")
      .notRequired(),
    formattedAddress: string()
      .label("ADDRESS")
      .required()
  }),
  siteNameUnassigned: bool()
    .default(false)
    .notRequired()
});
export type GeneralSettings = InferType<typeof GeneralSettingsSchema>;

export const SettingsHeader: React.FC<{
  title: string;
  userCanEdit: boolean;
}> = ({ title, userCanEdit }) => {
  const { t } = useTranslation();

  return (
    <div className="grid">
      <div className="col-6 col-xl-8">
        <CategoryTitle title={t("SETTINGS")} subtitle={title} />
      </div>
      <div className="col-6 col-xl-4 text-right pr-0">
        {userCanEdit && (
          <>
            <Field name="reset">
              {({ form: { dirty, handleReset, isSubmitting } }: FieldProps) => (
                <button
                  type="button"
                  className="btn"
                  disabled={isSubmitting || !dirty}
                  onClick={handleReset}
                >
                  {t("DISCARD")}
                </button>
              )}
            </Field>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  disabled={!dirty || !isValid}
                />
              )}
            </Field>
          </>
        )}
      </div>
    </div>
  );
};

const GeneralSiteSettings: React.FC<{
  id: string;
}> = ({ id }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const [, updateSite] = useUpdateSiteMutation();
  const [addNotification] = useNotification();
  const tenantId = useTenantId();
  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const { data, fetching, executor: refresh } = useSiteNameAndAddressQuery({
    variables: {
      id
    }
  });

  if (fetching) {
    return <LoadingIndicator />;
  }

  const name = data?.site?.name || "";
  const address = data?.site?.address || {
    address1: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
    geohash: "",
    formattedAddress: ""
  };

  if (
    nameIsUnassigned(name) &&
    !data?.site?.address?.formattedAddress &&
    !data?.site?.address?.address1
  ) {
    return <Redirect to="/manage/sites" />;
  }

  const initialValues = {
    siteName: name || "",
    siteAddress: {
      address1: address.address1 || "",
      city: address.city || "",
      region: address.region || "",
      postalCode: address.postalCode || "",
      country: address.country || "",
      geohash: address.geohash || "",
      formattedAddress: address.formattedAddress || ""
    },
    siteNameUnassigned: nameIsUnassigned(name)
  };

  const handleSubmit = async (values: GeneralSettings) => {
    const siteResponse = await updateSite({
      site: {
        id,
        tenantId,
        name: values.siteName,
        address: values.siteAddress
      }
    });
    if (siteResponse.data?.upsertSite) {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_UPDATE_SITE"),
        dismiss: DISMISS.DEFAULT
      });
      refresh({ requestPolicy: "network-only" });
    }
  };

  return (
    <Content className="settings">
      <Formik
        name="SiteGeneralSettings"
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={GeneralSettingsSchema}
        enableReinitialize
      >
        {({ setValues, dirty, values }) => {
          return (
            <BlockingForm unsavedChanges={dirty}>
              <Form className="form grid">
                <SettingsHeader
                  title={initialValues.siteName}
                  userCanEdit={userCanEdit}
                />
                <SettingsCategory title={t("GENERAL")}>
                  <FormInput
                    label={t("SITE_NAME")}
                    name="siteName"
                    tabIndex={1}
                    disabled={!userCanEdit || initialValues.siteNameUnassigned}
                  />
                  <div className="form-group">
                    <label className="col-4 text-right" id={"address-input"}>
                      {t("ADDRESS")}:
                    </label>
                    <div className="col-4">
                      <AddressInput
                        tabIndex={2}
                        name="siteAddress.formattedAddress"
                        disabled={!userCanEdit}
                        onSelect={address => {
                          const {
                            buildingNumber,
                            street,
                            countryName,
                            regionCode,
                            city,
                            zip,
                            geohash,
                            formattedAddress
                          } = address;
                          setValues({
                            ...values,
                            siteAddress: {
                              address1: street
                                ? [buildingNumber, street].join(" ")
                                : "",
                              country: countryName || "",
                              region: regionCode || "",
                              city: city || "",
                              postalCode: zip || "",
                              geohash: geohash || "",
                              formattedAddress: formattedAddress || ""
                            }
                          });
                        }}
                      />
                    </div>
                  </div>
                </SettingsCategory>
              </Form>
            </BlockingForm>
          );
        }}
      </Formik>
    </Content>
  );
};

export default GeneralSiteSettings;
